import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import {
  PUSH_START_GRACE_MS,
  stAdjustmentLinesRunKey,
  stAdjustmentsSummaryKey,
  useStAdjustmentPushStatus,
  useStartStAdjustmentPush,
} from "./query";
import {
  batchId,
  hasWorkerReported,
  hasWorkerStarted,
  isPushInFlight,
  isPushTerminal,
} from "./utils";

// One evolving toast per run: queued → complete (or failed). Derived from the id
// rather than held in state, so a cleanup can still address the run it belongs
// to after the hook has moved on to another one.
const pushToastIdFor = (runId: string | null) =>
  `st-push-${runId ?? "unknown"}`;

interface StartOptions {
  // Push these technicians' pending lines (server-side, no key enumeration).
  // Omitted pushes every pending line in the run — also server-side.
  technicianIds?: number[];
  // How many lines we expect the worker to attempt, for the queued toast.
  expected: number;
  // Called once the enqueue has settled, however it settled — success, failure,
  // or refused because a push is already in flight. The confirmation dialog uses
  // it to stay open, and locked, for exactly as long as the request is running.
  onSettled?: () => void;
}

// Owns everything about "a push is happening" for one run: enqueuing it, the
// starting gap, live progress, the single evolving toast, the completion
// announcement, and the cache invalidations that follow.
//
// Mount this ONCE per run. A second instance would carry its own idea of what is
// in flight, and the enqueue-to-worker-start gap below is exactly the window a
// duplicate push slips through. The PushPay section provider is that one mount.
//
// A null `runId` idles the status query when the section is not active.
export const useStAdjustmentPush = (
  businessId: string | null,
  runId: string | null,
) => {
  const queryClient = useQueryClient();
  // True between enqueuing a push and the worker recording its first progress.
  // The server still reports the PREVIOUS state throughout, so this is the only
  // thing that knows a push is on its way.
  const [awaitingStart, setAwaitingStart] = useState(false);
  // A push whose slot was reserved but whose worker never stamped `started_at`
  // inside the grace window. The server's `queued` record outlives us, so this is
  // what stops it reading as an active push for the rest of the session.
  const [startAbandoned, setStartAbandoned] = useState(false);
  // The `finished_at` on record when we enqueued, so a leftover completion from
  // an earlier batch isn't mistaken for this one starting. `undefined` means no
  // status had loaded yet, i.e. we have no baseline to compare against.
  const previousFinishedAtRef = useRef<string | null | undefined>(null);
  const announcedBatchRef = useRef<string | null>(null);
  const hasReadStatusRef = useRef(false);
  // React mutation state updates after `start` returns. Reserve the slot now so
  // two calls in the same event cannot enqueue two pushes.
  const startReservedRef = useRef(false);
  // Which run this hook currently represents. An enqueue can resolve after the
  // section has moved to another period, and this hook outlives that move (it
  // lives in the section provider, not behind a keyed boundary), so a callback
  // has to be able to tell whether it still belongs here.
  const runKey = `${businessId ?? ""}:${runId ?? ""}`;
  const runKeyRef = useRef(runKey);
  runKeyRef.current = runKey;

  const { data: progress } = useStAdjustmentPushStatus(
    businessId,
    runId,
    awaitingStart,
  );
  const { mutate: startPush, isPending: isEnqueuing } =
    useStartStAdjustmentPush(businessId, runId);

  const status = progress?.status ?? "idle";
  // A slot reservation we gave up on still reads as `queued` on the server until
  // its cache entry expires, so in-flight-ness alone would keep the UI pinned to
  // "Pushing…" forever. Once the worker genuinely starts, that overrides.
  const isPushing =
    hasWorkerStarted(progress) || (isPushInFlight(status) && !startAbandoned);
  const isStarting = awaitingStart && !isPushing;
  const isActive = isStarting || isPushing;
  const isBusy = isEnqueuing || isActive;
  const pushToastId = pushToastIdFor(runId);

  // Latest values for the unmount/timeout paths, which fire outside the render
  // that produced them.
  const progressRef = useRef(progress);
  const isActiveRef = useRef(false);
  const isBusyRef = useRef(false);
  useEffect(() => {
    progressRef.current = progress;
    isActiveRef.current = isActive;
    isBusyRef.current = isBusy;
  }, [progress, isActive, isBusy]);

  // This used to be remounted per run by a keyed view boundary, which reset it
  // for free. It now lives in the section provider, which outlives a run change,
  // so the reset has to be explicit: a stale `awaitingStart` would otherwise pin
  // the next run to "Pushing…", and a stale announced batch would swallow its
  // first completion. Declared before the effects that read these, so a run
  // change clears them in the same commit that those re-run in.
  useEffect(() => {
    setAwaitingStart(false);
    setStartAbandoned(false);
    previousFinishedAtRef.current = null;
    announcedBatchRef.current = null;
    hasReadStatusRef.current = false;
    startReservedRef.current = false;
  }, [businessId, runId]);

  // A push that outlives our watch on the run — the user switched period or tab
  // while it ran. Once we stop watching, nothing remains to resolve its spinner.
  useEffect(
    () => () => {
      if (!isActiveRef.current) return;

      toast.dismiss(pushToastIdFor(runId));
      toast.info("Push still running in the background", {
        description:
          "Reopen this pay period's PushPay tab to see how it finished.",
      });
    },
    [businessId, runId],
  );

  // Stop waiting once the worker reports on THIS batch.
  useEffect(() => {
    if (!awaitingStart || !progress) return;

    let baseline = previousFinishedAtRef.current;
    if (baseline === undefined) {
      // Nothing had loaded when we enqueued, so this first read is as likely to
      // be the previous batch's record as our own. Adopt it as the baseline
      // rather than reading it as a report — unless the worker is visibly at
      // work, which a finished batch never is.
      if (!isPushInFlight(progress.status)) {
        previousFinishedAtRef.current = progress.finished_at ?? null;
        return;
      }
      baseline = null;
      previousFinishedAtRef.current = null;
    }

    if (!hasWorkerReported(progress, baseline)) return;

    setAwaitingStart(false);
  }, [awaitingStart, progress]);

  // Give up after the grace window so a job that died before recording progress
  // doesn't leave the UI spinning forever. This needs a timer of its own: the
  // status poll can stop producing updates entirely (a failed request leaves the
  // data untouched), so nothing else would ever re-check the deadline.
  useEffect(() => {
    if (!awaitingStart) return;

    const timer = setTimeout(() => {
      startReservedRef.current = false;
      setAwaitingStart(false);
      setStartAbandoned(true);
      toast.error("Couldn't confirm the push started", {
        id: pushToastId,
        description:
          "The job was queued but hasn't reported any progress. Check this PushPay page, or push again. Lines that already landed are skipped.",
      });
    }, PUSH_START_GRACE_MS);

    return () => clearTimeout(timer);
  }, [awaitingStart, pushToastId]);

  // Announce a finished batch exactly once, and refresh what it invalidated.
  // Keyed on `finished_at` so a repeat push announces again, and seeded on the
  // first status read so a batch that finished before this opened stays history
  // instead of toasting on open.
  useEffect(() => {
    if (!progress) return;

    const batch = batchId(progress);
    const isFirstRead = !hasReadStatusRef.current;
    hasReadStatusRef.current = true;

    if (!isPushTerminal(progress.status)) return;
    if (isFirstRead) {
      announcedBatchRef.current = batch;
      return;
    }
    if (announcedBatchRef.current === batch) return;
    announcedBatchRef.current = batch;
    startReservedRef.current = false;

    // Both the rollup counts and the per-line "Pushed" badges are stale now —
    // true of a crashed job too, which may have landed lines before it died.
    queryClient.invalidateQueries({
      queryKey: stAdjustmentsSummaryKey(businessId, runId),
    });
    queryClient.invalidateQueries({
      queryKey: stAdjustmentLinesRunKey(businessId, runId),
    });

    // The job itself raised, as opposed to individual lines being rejected by
    // ServiceTitan. Whatever it managed before dying is already pushed, so a
    // retry picks up where it stopped rather than double-paying.
    if (progress.status === "failed") {
      toast.error("Push failed", {
        id: pushToastId,
        description:
          progress.error ??
          "The push stopped before finishing. Pushing again re-attempts only the lines that haven't landed.",
      });
      return;
    }

    if (progress.failed > 0) {
      toast.error("Push finished with failures", {
        id: pushToastId,
        description: `${progress.pushed} pushed, ${progress.failed} failed. Pushing again re-attempts only the failures.`,
      });
    } else {
      toast.success("Push complete", {
        id: pushToastId,
        description: `${progress.pushed} ${progress.pushed === 1 ? "adjustment" : "adjustments"} pushed with PushPay.`,
      });
    }
  }, [progress, businessId, runId, queryClient, pushToastId]);

  const start = useCallback(
    (options: StartOptions) => {
      const { technicianIds, expected, onSettled } = options;
      // Refused: something is already in flight. Settle anyway so a caller
      // waiting on this — the confirmation dialog — is not left open forever.
      if (startReservedRef.current || isBusyRef.current) {
        onSettled?.();
        return;
      }

      startReservedRef.current = true;
      // The run as it was when this request went out. Both callbacks below
      // compare against it before touching state.
      const startedFor = runKeyRef.current;
      startPush(
        { technicianIds },
        {
          onSuccess: () => {
            // The period moved on while this was in flight. Writing state now
            // would mark the NEW run as awaiting a start it never made, and its
            // status poll would read that as a live push — blocking a real one
            // until the grace window expires. Report the outcome against the run
            // it belongs to and leave this one alone.
            onSettled?.();
            if (runKeyRef.current !== startedFor) {
              toast.info("Push started in the background", {
                description:
                  "You changed period while it was starting. Reopen that period's PushPay tab to see how it finished.",
              });
              return;
            }

            setAwaitingStart(true);
            setStartAbandoned(false);
            // Baseline against what's on record now, so this batch's completion is
            // told apart from an earlier one's. With no status read yet there is
            // nothing to baseline against — the first read takes that job.
            const currentProgress = progressRef.current;
            previousFinishedAtRef.current = currentProgress
              ? (currentProgress.finished_at ?? null)
              : undefined;
            // Baseline the announcement too, so this batch's completion is
            // announced even if it lands before the first status read resolves.
            hasReadStatusRef.current = true;
            announcedBatchRef.current = batchId(currentProgress);
            toast.loading("PushPay is running…", {
              id: pushToastId,
              description: `${expected} ${expected === 1 ? "adjustment" : "adjustments"} queued. You can keep working. We'll tell you when it's done.`,
            });
          },
          onError: (error: unknown) => {
            onSettled?.();
            // Still worth telling the user it failed, but the reservation belongs
            // to the run that made it — this one's was already cleared by the
            // run-change reset.
            if (runKeyRef.current === startedFor)
              startReservedRef.current = false;
            toast.error("Failed to start push", {
              id: pushToastId,
              description: getErrorMessage(error),
            });
          },
        },
      );
    },
    [startPush, pushToastId],
  );

  return {
    start,
    isEnqueuing,
    isActive,
    // Blocks a second push while one is in flight.
    isBusy,
  };
};
